import { AdminLayout } from "@/components/layout/AdminLayout";
import { CampaignForm, CampaignFormValues } from "@/components/forms/CampaignForm";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { generateVoucherCode, batchArray } from "@/utils/voucherUtils";

export default function CreateCampaign() {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState<CampaignFormValues | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  const handleFormSubmit = (values: CampaignFormValues) => {
    setFormValues(values);
    setShowConfirmDialog(true);
  };
  
  const handleCreateCampaign = async () => {
    if (!formValues) return;
    
    try {
      setIsCreating(true);
      console.log("Creating campaign with values:", formValues);
      
      // Determine status based on isDraft and dates
      const now = new Date();
      let status: 'draft' | 'active' | 'scheduled' = 'draft';
      
      if (!formValues.isDraft) {
        const startDate = new Date(formValues.startDate);
        if (startDate <= now) {
          status = 'active';
        } else {
          status = 'scheduled';
        }
      }
      
      // Validate required fields
      if (!formValues.title.trim()) {
        throw new Error("Campaign title is required");
      }
      
      if (!formValues.description.trim()) {
        throw new Error("Campaign description is required");
      }
      
      if (!formValues.startDate || !formValues.endDate) {
        throw new Error("Start and end dates are required");
      }
      
      if (formValues.endDate < formValues.startDate) {
        throw new Error("End date must be after start date");
      }
      
      // Insert campaign into Supabase
      const campaignData = {
        title: formValues.title.trim(),
        description: formValues.description.trim(),
        campaign_type: formValues.campaignType,
        vouchers_total: formValues.voucherCount,
        start_date: formValues.startDate.toISOString(),
        end_date: formValues.endDate.toISOString(),
        voucher_image: formValues.voucherImage || null,
        voucher_details: formValues.voucherDetails.trim(),
        sponsor_name: formValues.sponsorName.trim(),
        sponsor_message: formValues.sponsorMessage?.trim() || null,
        status: status,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      console.log("Attempting to insert campaign with data:", campaignData);
      
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .insert(campaignData)
        .select()
        .single();
      
      if (campaignError) {
        console.error("Error creating campaign:", campaignError);
        if (campaignError.code === '23505') { // Unique violation
          toast.error("A campaign with this title already exists");
        } else if (campaignError.code === '23514') { // Check violation
          toast.error("Invalid campaign data. Please check your input.");
        } else if (campaignError.code === '42501') { // Permission denied
          toast.error("You don't have permission to create campaigns. Please check your authentication.");
        } else {
          toast.error(`Failed to create campaign: ${campaignError.message}`);
        }
        throw campaignError;
      }
      
      console.log("Campaign created successfully:", campaign);
      
      // Add venue connections
      if (formValues.venues.length > 0) {
        const campaignVenues = formValues.venues.map(venueId => ({
          campaign_id: campaign.id,
          venue_id: venueId
        }));
        
        console.log("Adding venue connections:", campaignVenues);
        
        const { error: venuesError } = await supabase
          .from('campaign_venues')
          .insert(campaignVenues);
        
        if (venuesError) {
          console.error("Error adding venue connections:", venuesError);
          if (venuesError.code === '42501') { // Permission denied
            toast.error("You don't have permission to add venue connections. Please check your authentication.");
          } else {
            toast.error("Failed to add venue connections");
          }
          throw venuesError;
        }
      }
      
      toast.success("Campaign created successfully!");
      navigate("/admin/campaigns");
    } catch (error) {
      console.error("Error in handleCreateCampaign:", error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Failed to create campaign. Please try again.");
      }
    } finally {
      setIsCreating(false);
      setShowConfirmDialog(false);
    }
  };
  
  return (
    <AdminLayout 
      title="Create Campaign" 
      subtitle="Set up a new promotional campaign"
    >
      <CampaignForm onSubmit={handleFormSubmit} />
      
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Campaign</DialogTitle>
            <DialogDescription>
              Are you sure you want to create this campaign?
              {formValues && !formValues.isDraft && (
                <span className="block mt-2 font-medium text-primary">
                  This will generate {formValues.voucherCount} voucher codes.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCampaign}
              disabled={isCreating}
            >
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isCreating ? "Creating..." : "Create Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
